<template>
  <div :class="$style.table">
    <div :class="$style.header">
      <div
        :class="$style.name"
        @click="toggleSort('title')"
      >
        {{ $t('pages.title') }}
        <div
          v-if="sortField === 'title'"
          :class="[$style.sort, { [$style.desc]: !isSortOrderAsc }]"
        >
          ▾
        </div>
      </div>
      <div
        :class="$style.nameUser"
        @click="toggleSort('user')"
      >
        {{ $t('pages.computerName') }}
        <div
          v-if="sortField === 'user'"
          :class="[$style.sort, { [$style.desc]: !isSortOrderAsc }]"
        >
          ▾
        </div>
      </div>
      <div
        :class="$style.ip"
        @click="toggleSort('ipAddress')"
      >
        {{ $t('pages.ipAddress') }}
        <div
          v-if="sortField === 'ipAddress'"
          :class="[$style.sort, { [$style.desc]: !isSortOrderAsc }]"
        >
          ▾
        </div>
      </div>
    </div>
    <computer
      v-for="item in treeSorted"
      :key="`${item.type}-${item.id}`"
      :computer="item"
      v-bind="item"
      :open-elements="openElements"
      :on-element-toggle="onElementToggle"
      @contextmenu="showMenu"
    />
    <context-menu
      v-show="isMenuVisible"
      id="contextmenu"
      :mouse-x="mouseX"
      :mouse-y="mouseY"
      @update="updateDangerLevel(currentComputer)"
    />
  </div>
</template>

<script>
  import ContextMenu from '@/components/common/contextmenu/reset-contextmenu.vue';
  import { getResetDangerLevel } from '@/api/methods/computers';
  import { TYPE_ITEM } from '@/utils/node-attributes';
  import Computer from './computer.vue';
  import { FILTERED_FIELDS } from './const/const';

  export function sortTree(tree, field, isAsc) {
    let currentField = field;

    return [...tree].sort((a, b) => {
      if (a.children && a.children.length) {
        a.children = sortTree(a.children, field, isAsc); // eslint-disable-line no-param-reassign
        currentField = 'title';
      }

      if (b.children && b.children.length) {
        b.children = sortTree(b.children, field, isAsc); // eslint-disable-line no-param-reassign
        currentField = 'title';
      }

      let result = 0;

      if (a.type !== b.type) {
        result = a.type === 'folder' ? -1 : 1;
      } else if (a[currentField] > b[currentField]) {
        result = 1;
      } else if (a[currentField] < b[currentField]) {
        result = -1;
      }

      return isAsc ? result : result * -1;
    });
  }

  export default {
    components: {
      Computer,
      ContextMenu,
    },
    props: {
      tree: {
        type: Array,
        required: true,
      },
      openElements: {
        type: Array,
        required: true,
      },
      onElementToggle: {
        type: Function,
        required: true,
      },
    },

    data: () => ({
      currentComputer: {},
      isMenuVisible: false,
      mouseX: null,
      mouseY: null,
      sortField: 'title',
      isSortOrderAsc: true,
    }),
    computed: {
      filteredTree() {
        const { computer } = this.$route.query;
        if (computer) {
          return this.filterTree(this.tree, computer);
        }
        return this.tree;
      },
      treeSorted() {
        const { sortField, isSortOrderAsc } = this;
        return sortTree(this.filteredTree, sortField, isSortOrderAsc);
      },
    },
    methods: {
      matchesFilter(item, search) {
        const query = search.trim().toLowerCase();

        return FILTERED_FIELDS.some((field) =>
          String(item[field] || '')
            .toLowerCase()
            .includes(query),
        );
      },
      filterTree(tree, search) {
        if (!search?.trim()) return tree;

        return tree.reduce((result, item) => {
          if (item.type === TYPE_ITEM.COMPUTER) {
            if (this.matchesFilter(item, search)) {
              result.push(item);
            }
            return result;
          }

          const groupMatched = this.matchesFilter(item, search);

          if (groupMatched) {
            result.push(item);
            return result;
          }

          const filteredChildren = this.filterTree(item.children || [], search);

          if (filteredChildren.length) {
            result.push({
              ...item,
              children: filteredChildren,
            });
          }

          return result;
        }, []);
      },
      async updateDangerLevel(computer) {
        await this.resetDangerLevel(computer);
        this.isMenuVisible = false;
        this.$emit('action');
      },
      async resetDangerLevel(computer) {
        const { id, nameUser } = computer;
        if (computer.children) {
          await Promise.all(computer.children.map((child) => this.resetDangerLevel(child)));
        } else {
          await getResetDangerLevel(id, nameUser);
        }
      },

      showMenu(event, computer) {
        if (computer.statusDangerous !== 0) {
          this.mouseX = event.clientX;
          this.mouseY = event.clientY;
          this.isMenuVisible = true;
          this.currentComputer = computer;

          window.addEventListener('click', (e) => {
            if (!document.getElementById('contextmenu').contains(e.target)) {
              this.isMenuVisible = false;
              window.removeEventListener('click', e);
            }
          });
        }
      },

      toggleSort(field) {
        if (this.sortField === field) {
          this.isSortOrderAsc = !this.isSortOrderAsc;
        } else {
          this.sortField = field;
          this.isSortOrderAsc = true;
        }
      },
    },
  };
</script>

<style lang="scss" module>
  .table {
    height: 100%;
    min-height: 0;
    overflow: auto;
    border: 1px solid $borderColor;
    border-left: none;
    border-right: none;
    border-bottom: none;
  }

  .header {
    background: $light-gray;
    color: $black;
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    user-select: none;
    display: flex;
    align-items: center;
    padding: 6px 0;
    position: sticky;
    top: 0;
    z-index: 3;
  }

  .name {
    cursor: pointer;
    flex: 1 1 100%;
    margin-left: 10px;
  }

  .nameUser {
    cursor: pointer;
    flex: 0 0 150px;
  }

  .ip {
    cursor: pointer;
    flex: 0 0 100px;
  }

  .sort {
    display: inline-block;
    vertical-align: baseline;
    margin-right: 4px;
    color: $dark-gray;

    &.desc {
      transform: rotate(180deg);
    }
  }
</style>
